import React, { useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useFormulaRunner from '../../../hooks/useFormulaRunner';
import { resolveUploads } from '../../../utils/forms.utils';
import { uploadFormsFile } from '../../../api/forms.api';
import { Box, Button, CircularProgress, FormControlLabel, MenuItem, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { Score } from '../../widgets';
import Weather from '../../widgets/Score/Weather/Weather';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { InnerProps } from '../dynamicFormTypes';
import FileField from './formFields/FileField';
import "./DynamicFormSkin1.scss";

const DynamicFormSkin1: React.FC<InnerProps> = ({
    formId,
    fields,
    schema,
    defaults,
    onSubmit,
    onValuesChange,
    apiSubmitting,
    saveButtonLabel = 'Submit',
    hasCancelButton = false,
    hasResetButton = false,
    onCancel,
    insetLabel,
    cancelLabel,
}) => {
    const {
        control,
        getValues,
        reset,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: defaults,
        mode: 'onBlur',
    });
    const formulaFields = useMemo(() => {
        const result: {
            id: string,
            formula: string,
        }[] = [];
        fields.forEach(field => {
            if (field.calculated && field.formula) {
                result.push({
                    id: field.id,
                    formula: field.formula,
                });
            }
        });

        return result;
    }, [ fields ]);
    const formulaRunner = useFormulaRunner();
    const watched = useWatch({ control });
    useEffect(() => {
        onValuesChange(watched || {});
    }, [ watched, onValuesChange ]);

    const submit: SubmitHandler<any> = async (vals) => {
        const formulaValues: {
            [key: string]: string
        } = {};
        formulaFields.forEach(formulaField => {
            formulaValues[formulaField.id] = getFormulaResult(formulaField.formula);
        });

        const uploadedFileValues = await resolveUploads(
            formId,
            fields,
            vals,
            uploadFormsFile,
        );

        await onSubmit({ ...vals, uploadedFileValues, ...formulaValues });
        if (!hasResetButton) {
            reset();
        }
    };

    const getFormulaResult = (formula: string): string => {
        const data = getValues();
        const result = formulaRunner(formula, data);
        return result as string;
    };

    const visibleFields = fields.filter(f => !f.hidden);

    return (
        <Box component="form" onSubmit={handleSubmit(submit)} noValidate>
            <Stack spacing={2} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {visibleFields.map((f) => {
                    if (f.type === 'image' || f.type === 'video' || f.type === 'document') {
                        return (
                            <Controller
                                key={f.id}
                                name={f.id}
                                control={control}
                                render={({ field }) => (
                                    <FileField
                                        value={field.value as File ?? null}
                                        label={f.label}
                                        error={errors[f.id]?.message as string}
                                        onChange={field.onChange}
                                        src={f.src}
                                    />
                                )}
                            />
                        );
                    }
                    if (f.type === 'widget' && f.typeBehavior === 'sliderBar') {
                        let score = '0';
                        const sourceField = fields.find(field => field.id === f.source);

                        if (sourceField) {
                            if (sourceField.formula) {
                                score = getFormulaResult(sourceField.formula);
                            } else {
                                const sourceValue = getValues()[f.source] as string;
                                score = sourceValue ?? '0';
                            }
                        }

                        return (
                            <div key={f.id} className='dynamic_form_score_container'>
                                <Score score={score}/>
                            </div>
                        );
                    }
                    if (f.type === 'widget' && f.typeBehavior === 'weather') {
                        let city = '';
                        const sourceField = fields.find(field => field.id === f.source);

                        if (sourceField) {
                            if (sourceField.formula) {
                                city = getFormulaResult(sourceField.formula);
                            } else {
                                const sourceValue = getValues()[f.source] as string;
                                city = sourceValue ?? '';
                            }
                        }

                        if (!city) {
                            return null;
                        }

                        return (
                            <div key={f.id} className='dynamic_form_weather_container'>
                                <Weather city={city}/>
                            </div>
                        );
                    }
                    if (f.type === 'string') {
                        return (
                            <div key={f.id} className='dynamic_form_string_container'>
                                <Box
                                    sx={{
                                        width: '100%',
                                        px: 2,
                                        py: 1.25,
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        borderRadius: 1,
                                        color: 'text.primary',
                                        fontSize: '1rem',
                                        fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                        lineHeight: '1.4375em',
                                    }}
                                >
                                    {f.label}
                                </Box>
                            </div>
                        );
                    }
                    if (f.type === 'text' || f.type === 'email' || f.type === 'number' || f.type === 'password') {
                        return (
                            <Controller
                                key={f.id}
                                name={f.id}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={f.label}
                                        type={f.type === 'number' ? 'number' : f.type}
                                        error={!!errors[f.id]}
                                        helperText={(
                                            errors[f.id]?.message as string
                                        ) || ''}
                                    />
                                )}
                            />
                        );
                    }
                    if (f.type === 'url') {
                        return (
                            <div key={f.id} className='dynamic_form_url_container'>
                                <Box
                                    sx={{
                                        width: '100%',
                                        px: 2,
                                        py: 1.25,
                                        color: 'text.primary',
                                        fontSize: '1rem',
                                        fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                        lineHeight: '1.4375em',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => window.open(f.label)}
                                >
                                    {f.label}
                                </Box>
                            </div>
                        );
                    }
                    if (f.type === 'select') {
                        return (
                            <Controller
                                key={f.id}
                                name={f.id}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label={f.label}
                                        error={!!errors[f.id]}
                                        helperText={(
                                            errors[f.id]?.message as string
                                        ) || ''}
                                        sx={{ width: 200 }}
                                    >
                                        {(
                                            f.options || []
                                        ).map((o) => (
                                            <MenuItem key={String(o.value)} value={o.value as any}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        );
                    }
                    if (f.type === 'radio') {
                        return (
                            <>
                                <Typography>{f.label}</Typography>
                                <Controller
                                    key={f.id}
                                    name={f.id}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioGroup {...field} row>
                                            {(
                                                f.options || []
                                            ).map((o) => (
                                                <FormControlLabel
                                                    key={String(o.value)}
                                                    value={o.value as any}
                                                    control={
                                                        <Radio/>
                                                    }
                                                    label={o.label}
                                                />
                                            ))}
                                        </RadioGroup>
                                    )}
                                />
                            </>
                        );
                    }
                    if (f.type === 'date') {
                        return (
                            <Controller
                                key={f.id}
                                name={f.id}
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        label={f.label}
                                        value={field.value ? dayjs(field.value as string | number | Date | Dayjs) : null}
                                        onChange={(v) => field.onChange(v ? (
                                            v as Dayjs
                                        ).toISOString() : null)}
                                        slotProps={{
                                            textField: {
                                                error: !!errors[f.id],
                                                helperText: (
                                                    errors[f.id]?.message as string
                                                ) || '',
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        );
                    }
                    if (f.type === 'color') {
                        return (
                            <Controller
                                key={f.id}
                                name={f.id}
                                control={control}
                                render={({ field }) => (
                                    <Box>
                                        <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                                            {f.label}
                                        </Typography>
                                        <TextField
                                            type="color"
                                            value={field.value || '#000000'}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                p: 0,
                                                '& .MuiInputBase-root': { p: 0, height: '100%', borderRadius: 1 },
                                                '& fieldset': { display: 'none' },
                                                '& input[type="color"]': {
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                    border: 'none',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'none',
                                                    borderRadius: 1,
                                                    WebkitAppearance: 'none',
                                                },
                                                '& input[type="color"]::-webkit-color-swatch-wrapper': {
                                                    padding: 0,
                                                    borderRadius: 1,
                                                },
                                                '& input[type="color"]::-webkit-color-swatch': {
                                                    border: '1px solid rgba(0,0,0,0.35)',
                                                    borderRadius: 4,
                                                },
                                            }}
                                        />
                                        {!!errors[f.id] && (
                                            <Typography variant="caption" color="error">
                                                {(
                                                    errors[f.id]?.message as string
                                                ) || ''}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            />
                        );
                    }
                    if (f.type === 'formula') {
                        return (
                            <div key={f.id} className='dynamic_form_formula_container'>
                                <div className='dynamic_form_formula_inside_container'>
                                    {f.label}
                                </div>
                                <div>
                                    <Box
                                        sx={{
                                            width: 266,
                                            px: 2,
                                            py: 1.25,
                                            border: '1px solid rgba(0, 0, 0, 0.23)',
                                            borderRadius: 1,
                                            color: 'text.primary',
                                            fontSize: '1rem',
                                            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                            lineHeight: '1.4375em',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        {getFormulaResult(f.formula)}
                                    </Box>
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
                {hasCancelButton ? (
                    <div className='dynamic_form_buttons_container'>
                        <Button
                            startIcon={isSubmitting || apiSubmitting ? <CircularProgress size={16}/> : undefined}
                            variant="outlined"
                            disabled={isSubmitting || apiSubmitting}
                            onClick={onCancel}
                            sx={{ width: '49%' }}
                        >
                            {cancelLabel}
                        </Button>
                        <Button
                            startIcon={isSubmitting || apiSubmitting ? <CircularProgress size={16}/> : undefined}
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || apiSubmitting}
                            sx={{ width: '49%', backgroundColor: '#013b71' }}
                        >
                            {insetLabel || saveButtonLabel}
                        </Button>
                    </div>
                ) : (
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || apiSubmitting}
                        startIcon={isSubmitting || apiSubmitting ? <CircularProgress size={16}/> : undefined}
                        style={{ backgroundColor: '#013b71' }}
                    >
                        {insetLabel || saveButtonLabel}
                    </Button>
                )}
            </Stack>
        </Box>
    );
};

export default DynamicFormSkin1;